import {Component, Inject, OnInit} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material';
import {DealerService} from 'app/authority/authority-shared/services/dealer.service';
import {CommodityModel} from '@shared/models/commodity.model';
import {NemoModuleKeys, UserPermissionsModel} from '@shared/models/user-permission.model';
import {AuthorityUsersPermissions} from '../../../../permissions-variables';
import {PermissionService} from '@shared/service/permission.service';
import {CommoditiesService} from '@shared/service/commodities.service';
import {SelectCommoditiesService} from '@shared/service/select-commodities.service';
import {CarBodyQuotationEntry, MechanicQuotationEntry} from '../../models/quotation-details.model';
import {DossiersService} from '../../services/dossiers.service';
import {DossierAndWorkingStates, DossierFlowType, ProfileType} from '../../models/enums.model';
import {LoaderService} from '@shared/service/loader.service';
import {InPageMessageType} from '@shared/components/in-page-message/in-page-message.component';
import {TranslateContext, TranslatePipe} from '@shared/pipes/translate.pipe';
import {QuotationEntry} from '../au-operation-working-detail/working-manager/working-manager.service';
import {MessageManagerService} from '@shared/service/message-manager/message-manager.service';
import {InnerMessageAuthorityMessageCode} from '../../services/inner-message-authority-utilities';
import {MessageComponentType, MessageType} from '@shared/service/message-manager/message.model';
import { DossierModel } from '../../models/dossier.model';

export interface AuManageDealerComponentData {
	sparePart: MechanicQuotationEntry | CarBodyQuotationEntry;
	dossier: DossierModel;
	dossierId: string;
	workingId: string;
	quotationId: string;
	workingStatus: string;
	dialog?: MatDialog;
	assignManyParts?: boolean;
	entries?: QuotationEntry[];
	slaEntriesIds?: string[];
	dossierFlowType?: string;
}

@Component({
	selector: 'amd-au-manage-dealer',
	templateUrl: './au-manage-dealer.component.html',
	styleUrls: ['./au-manage-dealer.component.scss']
})

export class AuManageDealerComponent implements OnInit {
	profile: UserPermissionsModel;
	sparePart: MechanicQuotationEntry | CarBodyQuotationEntry;
	dossier: DossierModel;
	dossierId: string;
	workingId: string;
	quotationId: string;
	dealerId: string;
	dossierFlowType: string;
	commodities: string[];

	dealer: CommodityModel;

	profileType: string;

	dealerForm: FormControl = new FormControl();
	options: string[]; // = ['One', 'Two', 'Three']; array di esempio - Rug
	filteredOptions: Observable<CommodityModel[]>;
	allDealers: CommodityModel[];
	authorityUsersPermissions: typeof AuthorityUsersPermissions = AuthorityUsersPermissions;
	showDealerModal: boolean;
	dialog: MatDialog;
	assignManyParts: boolean;
	selectedEntries: QuotationEntry[];
	workingStatus: string;
	dossierStatus: string;
	inPageMessageType: typeof InPageMessageType = InPageMessageType;
	translateContext: TranslateContext = TranslateContext.AUTHORITY;
	showLoader: boolean = false;
	slaEntriesIds: string[];
	dossiersFlowType: typeof DossierFlowType = DossierFlowType;
	workingStatusListWithSearchAllDealersDenied: string[] = ['REJECTED', 'CLOSED_WITH_LIQUIDATION', DossierAndWorkingStates.C_FLOW_REJECTED,
		'SUPERVISOR_REJECTED', DossierAndWorkingStates.CLOSED, 'CLOSED_WITH_REJECTION', 'PRE_AUTHORITY_REJECTED'];

	get canEdit(): boolean {
		if (!!this.slaEntriesIds && this.slaEntriesIds.length > 0) {
			return this.profileType === ProfileType.ALD;
		}

		return this.profile.hasPermissions(this.authorityUsersPermissions.authority_dealers_update)
		&& this.profileType === ProfileType.ALD
		&& (this.workingStatus === DossierAndWorkingStates.APPROVING ||
			this.workingStatus === DossierAndWorkingStates.RE_APPROVING ||
			this.workingStatus === DossierAndWorkingStates.SUPERVISOR_APPROVING
		) && !this.assignManyParts ? this.sparePart.isNew : this.assignManyParts;
	}

	constructor(
		public dialogRef: MatDialogRef<AuManageDealerComponent>,
		public messageManagerService: MessageManagerService,
		private dealerService: DealerService,
		private loaderService: LoaderService,
		private commService: CommoditiesService,
		private permissionService: PermissionService,
		private selectCommsService: SelectCommoditiesService,
		@Inject(MAT_DIALOG_DATA) private data: AuManageDealerComponentData,
		private dossiersService: DossiersService,
		private translatePipe: TranslatePipe
	) {
		this.sparePart = this.data.sparePart;
		this.dossierId = this.data.dossierId;
		this.dossier = this.data.dossier;
		this.workingId = this.data.workingId;
		this.quotationId = this.data.quotationId;
		this.dialog = this.data.dialog;
		this.assignManyParts = this.data.assignManyParts;
		this.selectedEntries = this.data.entries;
		this.workingStatus = this.data.workingStatus;
		this.dossierFlowType = this.data.dossierFlowType;

		this.profile = this.permissionService.getCurrentUserPermissions();
		this.slaEntriesIds = this.data.slaEntriesIds;
	}

	ngOnInit(): void {
		this.commodities = this.selectCommsService.getIDCommodities();
		this.setProfile();

		if (!this.assignManyParts) {
			if (this.dossierFlowType !== DossierFlowType.REPAIR) {
				this.getDealerDetail();
			} else {
				this.messageManagerService.pushInnerMessage({
					code: InnerMessageAuthorityMessageCode.AUTHORITY_CAN_T_ASSOCIATE_DEALER_FOR_REPAIR,
					componentType: MessageComponentType.FLASH_MESSAGE,
					type: MessageType.TYPE_ERROR,
					isTop: false
				});
				this.showDealerModal = true;
			}
		} else if (this.dossierFlowType !== DossierFlowType.REPAIR) {
			this.dealerForm.setValue(undefined);
		} else {
			this.messageManagerService.pushInnerMessage({
				code: InnerMessageAuthorityMessageCode.AUTHORITY_CAN_T_ASSOCIATE_DEALER_FOR_REPAIR,
				componentType: MessageComponentType.FLASH_MESSAGE,
				type: MessageType.TYPE_ERROR,
				isTop: false
			});
			this.showDealerModal = true;
		}

		if (
			this.profileType !== ProfileType.ASSISTANCE_CENTER &&
			!this.workingStatusListWithSearchAllDealersDenied.includes(this.workingStatus) &&
			this.dossierFlowType !== DossierFlowType.REPAIR && this.profile.hasPermissions(AuthorityUsersPermissions.authority_dealers_update)) {
			this.showDealerModal = false;
			this.commService.getAll(this.dossierId, this.workingId, this.quotationId,
				response => {
					if (response) {
						this.allDealers = response.commodities;
						this.filteredOptions = this.dealerForm.valueChanges.pipe(
							startWith(''),
							map(dealer => typeof dealer === 'string' ? dealer : this.getDealerValueToShow(dealer)),
							map(dealer => dealer ? this._filter(dealer) : this.allDealers));
						this.showDealerModal = true;
					}
				},
				message => {
					this.messageManagerService.pushInnerMessage(message);
					this.showDealerModal = true;
					this.dialogRef.close(null);
				}
			);
		}
	}

	private _filter(value: string): CommodityModel[] {
		if (value) {
			return this.allDealers.filter(option => this.getDealerValueToShow(option).toLowerCase().includes(value.toLowerCase()));
		}
		return this.allDealers;
	}

	getDealerDetail(): void {
		this.showDealerModal = false;
		this.dealerService.getDealer(this.dossier, this.workingId, this.quotationId, this.sparePart.id, dealer => {
				this.dealer = dealer;
				if (this.dealer) {
					this.dealerForm.setValue(this.dealer);
				}
				this.showDealerModal = true;
			}, () => {
				// this.innerMessagesAuthorityService.pushInnerMessage(message);
				this.showDealerModal = true;
				// this.dialog.closeAll();
			}
		);
	}

	getDealerValueToShow(dealer: CommodityModel): string {
		switch (dealer) {
			case null:
				return null;
			case undefined:
				return undefined;
			default :
				return this.getDealerDisplayValue(dealer);
		}
	}

	setProfile(): void {
		if (this.permissionService.getCurrentUserPermissions().isAldOperator(NemoModuleKeys.AUTHORITY)) {
			this.profileType = ProfileType.ALD;
		} else if (this.permissionService.getCurrentUserPermissions().isCenterOperator(NemoModuleKeys.AUTHORITY)) {
			this.profileType = ProfileType.ASSISTANCE_CENTER;
		}
	}

	saveDealerAction(): void {
		if (this.assignManyParts) {
			this.saveManyDealer();
		} else {
			if (!this.slaEntriesIds) {
				this.saveDealer();
			} else {
				this.saveSlaDealer();
			}
		}
	}

	saveDealer(): void {
		this.showLoader = true;
		this.dealerService.setDealerByEntry(this.dossierId, this.workingId, this.quotationId, this.sparePart.id, this.dealerId, this.commodities,
			successMessage => {
				this.messageManagerService.pushInnerMessage(successMessage);
				this.dossiersService.getDossierRefresher().next();
				this.dialogRef.close({success: true});
				this.showLoader = false;
			},
			failureMessage => {
				this.showLoader = false;
				this.messageManagerService.pushInnerMessage({...failureMessage, ...{isTop: false}});
			}
		);
	}

	saveManyDealer(): void {
		const selectedEntriesId: string[] = [];
		this.selectedEntries.filter(el => el.isForDealer && !el.warrantyType).forEach(entry => selectedEntriesId.push(entry.id));
		this.showLoader = true;
		this.dealerService.setDealerByEntries(this.dossierId, this.workingId, this.quotationId, selectedEntriesId, this.dealerId, this.commodities,
			successMessage => {
				this.messageManagerService.pushInnerMessage(successMessage);
				this.dossiersService.getDossierRefresher().next();
				this.dialogRef.close({success: true});
				this.showLoader = false;
			},
			failureMessage => {
				this.showLoader = false;
				this.messageManagerService.pushInnerMessage({...failureMessage, ...{isTop: false}});
			}
		);
	}

	saveSlaDealer(): void {
		this.showLoader = true;
		this.dealerService.setSlaDealerByElapsedOrderId(this.dossierId, this.workingId, this.quotationId, this.slaEntriesIds, this.dealerId, this.commodities,
			successMessage => {
				this.messageManagerService.pushInnerMessage(successMessage);
				this.dossiersService.getDossierRefresher().next();
				this.dialogRef.close({success: true});
				this.showLoader = false;
			},
			failureMessage => {
				this.showLoader = false;
				this.messageManagerService.pushInnerMessage({...failureMessage, ...{isTop: false}});
			}
		);
	}

	selectCommodity(selected: boolean, dealer: CommodityModel): void {
		if (selected) {
			dealer ? this.dealerId = dealer.id : this.dealerId = null;
		}
	}

	displayFn(dealer?: CommodityModel): string | undefined {
		if (dealer === null) {
			return this.translatePipe.transform('AUTO_APPROVVIGIONAMENTO', TranslateContext.AUTHORITY);
		}
		return !!dealer ? `${dealer.name} ${dealer.ald_commodity_id} (${dealer.cod_axus}) - ${dealer.address}, ${dealer.address_number}, ${dealer.location}`
			: undefined;
	}

	getDealerDisplayValue(dealer: CommodityModel): string {
		return `${dealer.name} ${dealer.ald_commodity_id} (${dealer.cod_axus}) - ${dealer.address}, ${dealer.address_number}, ${dealer.location}`;
	}
}
